import React, { useRef, useEffect } from 'react';

export default function SharedLogsPanel({
  logs = [],
  usageData,
  liveTokens,
  extensionStatus,
  onExtend,
  onClear,
  onExpand,
  logsRef,
  logsOnly,   // render only the logs section (no token usage)
  tokenOnly,  // render only the token usage section (no logs)
}) {
  const logsEndRef = useRef();
  useEffect(() => {
    if (logsEndRef.current) logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const requests = usageData ? (usageData.requestCount    || 0)                  : 0;
  // Company-pool plans: the bar is the whole company's monthly shared pool, while
  // the stats below are this user's own usage today — so label them distinctly.
  const isPool = liveTokens?.strategy === 'company_pool';
  // Unlimited plans (or super-admins) have no cap — show "Unlimited" instead of a 0/0 bar.
  const isUnlimited = liveTokens?.strategy === 'unlimited';
  // console.log('%c[SharedLogsPanel] render → liveTokens =', 'color:#2EBFA5;font-weight:700', liveTokens, '| limit:', liveTokens?.limit, '| strategy:', liveTokens?.strategy, '| isUnlimited:', isUnlimited, '| isPool:', isPool);
  // Total = input + output (matches the admin panel's "Tokens (I+O)"; cache read excluded)
  const total    = usageData
    ? ((usageData.inputTokens || 0) + (usageData.outputTokens || 0)).toLocaleString()
    : '0';

  return (
    <React.Fragment>
      {!tokenOnly && (
        <div className="sh-logs-panel" ref={logsRef}>
          <div className="sh-logs-header">
            <span className="sh-dash-title">Live Logs</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {onClear && (
                <button className="sh-logs-expand" onClick={onClear} title="Clear logs"
                  style={{ fontSize: 11, padding: '1px 7px' }}>
                  clear
                </button>
              )}
              {onExpand && (
                <button className="sh-logs-expand" onClick={onExpand} title="View full logs">⤢</button>
              )}
            </div>
          </div>
          <div className="sh-logs-box">
            {logs.length === 0 ? (
              <div style={{ color: '#3d5273', fontSize: 11, padding: '16px 8px', fontFamily: 'monospace', textAlign: 'center' }}>
                Logs will appear here during analysis.
              </div>
            ) : logs.map((log, i) => (
              <div key={i} className="sh-log-row">
                <span className="sh-log-ts">[{log.ts || log.time}]</span>
                <span className={`sh-log-lvl ${log.level || 'info'}`}>{(log.level || 'info').toUpperCase()}</span>
                <span className="sh-log-msg">{log.msg}</span>
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        </div>
      )}

      {!logsOnly && (
        <div className="sh-usage-panel">
          <div className="sh-logs-header">
            <span className="sh-dash-title">Token Usage</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {!isUnlimited && (extensionStatus === 'pending' ? (
                <span className="sh-usage-extend-inline sh-usage-extend-inline--pending">⏳ Pending…</span>
              ) : (extensionStatus !== 'approved' || !usageData?.extensionTokens) && (
                <button className="sh-usage-extend-inline" onClick={onExtend} disabled={!onExtend} title="Request 500K Token Extension">
                  ＋ Extend
                </button>
              ))}
              <span className="sh-usage-date">{usageData?.date || 'Today'}</span>
            </div>
          </div>
          <div className="sh-usage-body">
            {liveTokens && (isUnlimited ? (
              <div className="sh-usage-limit-block">
                <div className="sh-usage-limit-row">
                  <span className="sh-usage-limit-label">Token Limit</span>
                  <span className="sh-usage-limit-counts" style={{ color: '#2EBFA5', fontWeight: 600 }}>∞ Unlimited</span>
                </div>
              </div>
            ) : (
              <div className="sh-usage-limit-block">
                <div className="sh-usage-limit-row">
                  <span className="sh-usage-limit-label">{isPool ? 'Monthly Pool · shared' : 'Daily Limit'}</span>
                  <span className="sh-usage-limit-counts">
                    <span style={{ color: liveTokens.percentUsed >= 80 ? (liveTokens.percentUsed >= 100 ? '#f44336' : '#ffa032') : '#2EBFA5', fontWeight: 600 }}>
                      {liveTokens.totalUsed.toLocaleString()}
                    </span>
                    <span style={{ color: '#555', margin: '0 3px' }}>/</span>
                    <span>{liveTokens.limit.toLocaleString()}</span>
                  </span>
                </div>
                <div className="sh-usage-bar-track">
                  <div className="sh-usage-bar-fill" style={{
                    width: `${Math.max(Math.min(liveTokens.percentUsed, 100), liveTokens.totalUsed > 0 ? 1 : 0)}%`,
                    background: liveTokens.percentUsed >= 100 ? '#f44336' : liveTokens.percentUsed >= 80 ? '#ffa032' : '#2EBFA5',
                  }} />
                </div>
                <div className="sh-usage-bar-meta">
                  <span>{liveTokens.percentUsed < 1 && liveTokens.totalUsed > 0 ? '< 1%' : `${liveTokens.percentUsed}%`} used</span>
                  <span>{liveTokens.remaining.toLocaleString()} remaining</span>
                </div>
              </div>
            ))}
            <div className="sh-usage-grid">
              <div className="sh-usage-stat">
                <span className="sh-usage-stat-val">{total}</span>
                <span className="sh-usage-stat-lbl">{isPool ? 'Your Tokens · today' : 'Total Tokens'}</span>
              </div>
              <div className="sh-usage-stat">
                <span className="sh-usage-stat-val">{requests}</span>
                <span className="sh-usage-stat-lbl">Requests</span>
              </div>
            </div>
            {usageData?.extensionTokens > 0 && extensionStatus === 'approved' && (
              <div className="sh-usage-cost-row">
                <span className="sh-usage-label">Extensions granted</span>
                <span className="sh-usage-value sh-usage-ext">+{(usageData.extensionTokens || 0).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </React.Fragment>
  );
}
